"use client";

import { useState } from "react";
import PhosphorIcon from "@/shared/components/common/PhosphorIcon";
import { HOTEL_RESULTS_V2_SORT_OPTIONS } from "../constants";
import type { HotelResultsV2SortValue, HotelResultsV2ViewMode } from "../types";

interface SortBarProps {
  sort: HotelResultsV2SortValue;
  viewMode: HotelResultsV2ViewMode;
  onSortChange: (sort: HotelResultsV2SortValue) => void;
  onViewModeChange: (viewMode: HotelResultsV2ViewMode) => void;
}

export default function SortBar({
  sort,
  viewMode,
  onSortChange,
  onViewModeChange,
}: SortBarProps) {
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const currentSort =
    HOTEL_RESULTS_V2_SORT_OPTIONS.find((option) => option.value === sort) ??
    HOTEL_RESULTS_V2_SORT_OPTIONS[0];
  const mobileSortOptions = HOTEL_RESULTS_V2_SORT_OPTIONS.filter((option) =>
    ["recommended", "price_asc", "price_desc"].includes(option.value),
  ).map((option) => ({
    ...option,
    label: option.value === "recommended" ? "پیش‌فرض" : option.label,
  }));

  return (
    <div className="flex shrink-0 items-center gap-2">
      <div className="md:hidden">
        <button
          onClick={() => setSortMenuOpen(true)}
          className="inline-flex shrink-0 snap-start items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold leading-none text-slate-950 transition-all hover:border-primary hover:text-primary"
          type="button"
        >
          <PhosphorIcon name="SortAscending" size={13} />
          مرتب سازی
          <PhosphorIcon name="CaretDown" size={12} />
        </button>

        {sortMenuOpen && (
          <div
            className="fixed inset-0 z-[320] flex items-end md:hidden"
            dir="rtl"
          >
            <button
              className="absolute inset-0 bg-black/45"
              onClick={() => setSortMenuOpen(false)}
              type="button"
              aria-label="بستن مرتب‌سازی"
            />
            <div className="relative w-full rounded-t-2xl bg-white px-4 pb-5 pt-4 shadow-[0_-12px_36px_rgba(15,23,42,0.18)]">
              <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-slate-300" />
              <div className="mb-3 flex items-center justify-between">
                <p className="text-base font-bold text-slate-950">مرتب سازی</p>
                <button
                  onClick={() => setSortMenuOpen(false)}
                  className="grid size-8 place-items-center rounded-full bg-slate-100 text-slate-500"
                  type="button"
                  aria-label="بستن"
                >
                  <PhosphorIcon name="X" size={15} />
                </button>
              </div>
              <div className="space-y-2">
                {mobileSortOptions.map((option) => {
                  const active = sort === option.value;

                  return (
                    <button
                      key={option.value}
                      onClick={() => {
                        onSortChange(option.value);
                        setSortMenuOpen(false);
                      }}
                      className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-right text-sm font-semibold transition ${
                        active
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-slate-200 bg-white text-slate-800 hover:border-primary"
                      }`}
                      type="button"
                    >
                      <span className="inline-flex items-center gap-2">
                        <PhosphorIcon name={option.icon} size={16} />
                        {option.label}
                      </span>
                      {active && (
                        <PhosphorIcon
                          name="CheckCircle"
                          size={18}
                          weight="fill"
                          className="text-primary"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="relative hidden md:block">
        <button
          onClick={() => setSortMenuOpen((value) => !value)}
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-950 transition hover:border-primary hover:text-primary"
          type="button"
        >
          <PhosphorIcon name="SortAscending" size={12} />
          مرتب سازی
          <PhosphorIcon name="CaretDown" size={11} />
        </button>

        {sortMenuOpen && (
          <>
            <button
              className="fixed inset-0 z-30 cursor-default"
              onClick={() => setSortMenuOpen(false)}
              type="button"
              aria-label="بستن مرتب‌سازی"
            />
            <div className="absolute right-0 top-full z-40 mt-1.5 min-w-40 overflow-hidden rounded-2xl border border-slate-200 bg-white py-1.5 text-right shadow-xl">
              {HOTEL_RESULTS_V2_SORT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    onSortChange(option.value);
                    setSortMenuOpen(false);
                  }}
                  className={`flex w-full items-center gap-2 px-4 py-2 text-xs transition ${
                    sort === option.value
                      ? "bg-slate-100 font-bold text-primary"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                  type="button"
                >
                  <PhosphorIcon name={option.icon} size={11} />
                  {option.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="hidden overflow-hidden rounded-full border border-slate-200 bg-white lg:flex">
        <button
          onClick={() => onViewModeChange("grid")}
          className={`grid p-2 transition ${
            viewMode === "grid"
              ? "bg-primary text-white"
              : "text-slate-400 hover:text-primary"
          }`}
          aria-label="نمایش گریدی"
          type="button"
        >
          <PhosphorIcon name="SquaresFour" size={14} />
        </button>
        <button
          onClick={() => onViewModeChange("list")}
          className={`grid p-2 transition ${
            viewMode === "list"
              ? "bg-primary text-white"
              : "text-slate-400 hover:text-primary"
          }`}
          aria-label="نمایش ردیفی"
          type="button"
        >
          <PhosphorIcon name="ListBullets" size={14} />
        </button>
      </div>
    </div>
  );
}
